import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WorksheetService } from 'src/app/features/worksheet/services/worksheet.service';
import { HistoryTestChange, NativeSoilHistoryEvent, NativeSoilHistoryRow } from 'src/app/features/worksheet/models/native-soil-worksheet.modal';
import { FormService } from 'src/app/shared/services/form.service';
import { plural } from 'src/app/shared/components/history-timeline/history-timeline.component';
import { HistoryChange } from 'src/app/shared/models/history-timeline.modal';

/**
 * Timeline of saves, assignments and TPA readings on the Thermal Resistivity (native soil) worksheet. Each entry
 * shows the rows it changed: the In-House Id, every weight/TR test with what happened to it (added, removed or
 * edited, with the previous values), and the row's assignment.
 */
@Component({
    selector: 'app-native-soil-history',
    templateUrl: './native-soil-history.component.html',
    styleUrls: ['./native-soil-history.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NativeSoilHistoryComponent implements OnInit {
  projectId: string | null = null;
  companyName = '';
  projectName = '';
  referenceId = '';
  // Newest first: the most recent action is the leftmost point on the timeline
  events: NativeSoilHistoryEvent[] = [];
  selectedIndex = -1;
  isLoading = true;
  // Same order as the Thermal Resistivity worksheet table
  displayedColumns = ['inHouseId', 'sampleId', 'effort', 'depth', 'labSpecMoistureContent', 'testNo', 'weight', 'tr', 'assignedTo'];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private worksheetService: WorksheetService,
    private formService: FormService,
  ) {}

  ngOnInit(): void {
    this.projectId = this.route.snapshot.queryParamMap.get('projectId');
    if (!this.projectId) {
      this.isLoading = false;
      return;
    }

    this.formService.fetchFormData(this.projectId, 'nativesoil').subscribe({
      next: (result) => {
        this.companyName = result?.company?.name ?? '';
        this.projectName = result?.project?.projectName ?? '';
        this.referenceId = result?.project?.referenceId ?? '';
      },
    });

    this.worksheetService.getNativeSoilHistory(this.projectId).subscribe({
      next: (events) => {
        // The API returns newest first, which is also the timeline's left-to-right order
        this.events = (Array.isArray(events) ? events : []).map((event) => ({ ...event, changes: this.buildChanges(event) }));
        this.isLoading = false;
        // Open on the latest action (the leftmost point)
        this.selectedIndex = this.events.length > 0 ? 0 : -1;
      },
      error: () => {
        this.events = [];
        this.isLoading = false;
      },
    });
  }

  get selectedEvent(): NativeSoilHistoryEvent | null {
    return this.events[this.selectedIndex] ?? null;
  }

  backToWorksheet(): void {
    this.router.navigate(['native-soil-worksheet'], { queryParams: { projectId: this.projectId } });
  }

  reDirectUrl(): void {
    this.router.navigate(['dashboard']);
  }

  // Effort 0 is a tube sample, shown as "Tube" like on the worksheet
  displayEffort(row: NativeSoilHistoryRow): string {
    return row.effort === 0 ? 'Tube' : `${row.effort}`;
  }

  displayDepth(row: NativeSoilHistoryRow): string {
    if (row.depth === null || row.depth === undefined) {
      return '';
    }
    return row.endDepth !== null && row.endDepth !== undefined ? `${row.depth}-${row.endDepth}` : `${row.depth}`;
  }

  displayValue(value: string | number | boolean | null | undefined): string {
    return value === null || value === undefined || value === '' ? '—' : `${value}`;
  }

  // Label next to a test's number; unchanged tests get none
  testChangeLabel(change: HistoryTestChange): string {
    return change === 'Unchanged' ? '' : change;
  }

  // Timeline icons: tests added, tests removed, edits (test values and In-House Id) and assignments, each with its count
  private buildChanges(event: NativeSoilHistoryEvent): HistoryChange[] {
    const tests = event.rows.flatMap((row) => row.tests);
    const countTests = (change: HistoryTestChange) => tests.filter((test) => test.change === change).length;
    const added = countTests('Added');
    const removed = countTests('Removed');
    const editedTests = countTests('Edited');
    const inHouseIds = event.rows.filter((row) => row.inHouseId.changed).length;
    const assigned = event.rows.filter((row) => row.assignedTo.changed).length;
    const firstSaves = event.rows.filter((row) => row.isFirstRecord).length;

    const changes: HistoryChange[] = [];
    if (added > 0) {
      changes.push({ kind: 'added', tooltip: `${plural(added, 'test')} added` });
    }
    if (removed > 0) {
      changes.push({ kind: 'removed', tooltip: `${plural(removed, 'test')} removed` });
    }
    const edits = [
      editedTests > 0 ? `${plural(editedTests, 'test')} edited` : '',
      inHouseIds > 0 ? `${plural(inHouseIds, 'In-House Id')} changed` : '',
      firstSaves > 0 ? `${plural(firstSaves, 'row')} saved for the first time` : '',
    ].filter(Boolean);
    if (edits.length > 0) {
      changes.push({ kind: 'edited', tooltip: edits.join(', ') });
    }
    if (assigned > 0) {
      changes.push({ kind: 'assigned', tooltip: `${plural(assigned, 'row')} assigned` });
    }
    return changes;
  }

  changedFieldCount(event: NativeSoilHistoryEvent): number {
    return event.rows.reduce(
      (total, row) =>
        total +
        row.tests.filter((test) => test.change !== 'Unchanged').length +
        (row.inHouseId.changed ? 1 : 0) +
        (row.assignedTo.changed ? 1 : 0),
      0,
    );
  }
}
